/* eslint-disable no-underscore-dangle */

import {
  getDefaultTooltipOption,
  getGripperConnectivity,
  isEndEffector,
} from '@sb/integrations/utils';
import { makeNamespacedLog } from '@sb/log';
import { FailureKind } from '@sb/routine-runner';
import type { StepPlayArguments } from '@sb/routine-runner/Step/Step';
import Step from '@sb/routine-runner/Step/Step';
import { wait } from '@sb/utilities';

import { EnableGripperArguments as Arguments } from './Arguments';
import { EnableGripperVariables as Variables } from './Variables';

const log = makeNamespacedLog('EnableGripperStep');

export class EnableGripperStep extends Step<Arguments, Variables> {
  public static areSubstepsRequired = false;

  public static Arguments = Arguments;

  public static Variables = Variables;

  public initializeVariableState(): void {
    this.variables = {
      succeeded: false,
    };
  }

  public async _play({ fail }: StepPlayArguments): Promise<void> {
    const {
      payload,
      deviceID,
      waitForGripperToInitialize,
      setActiveTooltip,
      activeTooltipOption,
    } = this.args;

    this.setVariable('succeeded', false);

    if (!deviceID) {
      return fail({
        failure: {
          kind: FailureKind.GripperFailure,
          message: `No device ID provided.`,
        },
        failureReason: `No device ID provided.`,
      });
    }

    const equipmentState = this.routineContext.equipment.getEquipmentState();

    const enabledGripper = equipmentState.find((item) =>
      isEndEffector(item.kind),
    );

    if (enabledGripper != null) {
      if (enabledGripper.id !== deviceID) {
        return fail({
          failure: {
            kind: FailureKind.GripperFailure,
            message: `An actuating gripper is already enabled.`,
          },
          failureReason: `An actuating gripper is already enabled.`,
        });
      }
    } else {
      try {
        await this.routineContext.disabledEquipment.enableEquipment(deviceID);
        // We wait for 500ms to ensure the gripper state has time to update
        await wait(500);
      } catch (error) {
        return fail({
          failure: {
            kind: FailureKind.GripperFailure,
            message: `Failed to enable gripper: ${error.message}`,
          },
          failureReason: `Failed to enable gripper: ${error.message}`,
          error,
        });
      }
    }

    if (waitForGripperToInitialize) {
      const maxWaitTime = 15000; // 15 seconds
      const pollInterval = 100; // 100ms
      const startTime = Date.now();

      while (Date.now() - startTime < maxWaitTime) {
        if (await this.shouldStopPlaying()) {
          return undefined;
        }

        const currentEquipmentState =
          this.routineContext.equipment.getEquipmentState();

        const newlyEnabledState = currentEquipmentState.find(
          (item) => isEndEffector(item.kind) && item.id === deviceID,
        )?.state;

        if (newlyEnabledState && getGripperConnectivity(newlyEnabledState)) {
          // Some grippers (e.g. Robotiq) require an activation/calibration
          // cycle after connecting. Wait for that to complete too.
          const isPendingActivation =
            'activated' in newlyEnabledState &&
            (newlyEnabledState as { activated?: boolean }).activated !== true;

          if (!isPendingActivation) {
            break;
          }
        }

        await wait(pollInterval);
      }

      const finalEquipmentState =
        this.routineContext.equipment.getEquipmentState();

      const finalGripper = finalEquipmentState.find(
        (item) => isEndEffector(item.kind) && item.id === deviceID,
      );

      const finalState = finalGripper?.state;

      const isConnected = getGripperConnectivity(finalState) === true;

      // Some grippers (e.g. Robotiq) require an activation/calibration
      const isPendingActivation =
        finalState &&
        typeof finalState === 'object' &&
        'activated' in finalState &&
        (finalState as { activated?: boolean }).activated !== true;

      if (!isConnected || isPendingActivation) {
        return fail({
          failure: {
            kind: FailureKind.GripperFailure,
            message: `Gripper did not connect or activate within the timeout period.`,
          },
          failureReason: `Gripper did not connect or activate within the timeout period.`,
        });
      }
    }

    if (payload) {
      await this.routineContext.setPayload(payload, this.routineFrame);
    }

    if (setActiveTooltip) {
      const device = this.routineContext.allEquipmentItems.find(
        (item) => item.id === deviceID,
      );

      const tcpOption =
        activeTooltipOption ??
        (device ? getDefaultTooltipOption(device.config) : undefined);

      if (tcpOption) {
        this.routineContext.setActiveTooltip({
          tcpOption,
          gripperID: deviceID,
        });
      } else {
        // The step asked for a tooltip and there is none to set — the
        // gripper offers none, or its row is gone. Validation reports this
        // at authoring time; this is the trace for a routine already running.
        log.warn(
          'setActiveTooltip.noTooltip',
          'Enable Gripper: no tooltip to set as active',
          { deviceID },
        );
      }
    }

    this.setVariable('succeeded', true);

    return undefined;
  }
}
